import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useSelector } from 'react-redux';
import useWebSocket from 'react-use-websocket';
import axios from '../utils/axios';
import { appSubmission, getCustomFields, getNotifications, getUserOpportunityCard, notificationFlag, notificationOpened, registeredUserList } from '../utils/urls';
import logo from '../Images/homes_sale_logo.png';
import { Toast } from 'primereact/toast';
import FlexComponent from '../components/FlexComponent';
import AppModal from './AppModal';
import AppPopup from '../components/apps/appsKanban/AppPopup';
import './Nav.css';
import './Notification.css';
import { Badge } from 'primereact/badge';
import { InputSwitch } from 'primereact/inputswitch';
import { InputTextarea } from 'primereact/inputtextarea';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import UserPopup from '../components/apps/usersKanban/UserPopup';

const NOTIFICATION_TABS = [
    { id: 'general', label: 'General alerts', short: 'general', emptyText: 'All your notifications will show up here.' },
    { id: 'file_details', label: 'File details', short: 'file details', emptyText: 'Document uploads for your clients will show up here.' },
    { id: 'file_communication', label: 'File communication', short: 'file communication', emptyText: 'Comments on files you watch and checklist updates will show up here.' },
    { id: 'help', label: 'Help', short: 'help', icon: '🚨', variant: 'help', emptyText: 'Help requests will show up here.' },
    { id: 'manager_review', label: 'Manager review', short: 'manager review', icon: '!', variant: 'review', emptyText: 'Notifications flagged by your team will show up here.' },
];

const pageSize = 50;
const jsonHeaders = { headers: { 'Content-Type': 'application/json' } };

const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileBadge = (fileName = '') => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop().toUpperCase().slice(0, 4) : 'FILE';
};

const dayLabel = (iso) => {
    const date = iso ? new Date(iso) : null;
    if (!date || isNaN(date)) return 'Earlier';
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

// Consecutive grouping is enough because the API returns newest first.
const groupByDay = (items) => items.reduce((groups, item) => {
    const label = dayLabel(item.created_at_iso);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
        last.items.push(item);
    } else {
        groups.push({ label, items: [item] });
    }
    return groups;
}, []);

const mergeById = (existing, incoming) => {
    const incomingById = new Map(incoming.map(n => [n.id, n]));
    const merged = existing.map(n => incomingById.get(n.id) || n);
    const seen = new Set(existing.map(n => n.id));
    return [...merged, ...incoming.filter(n => !seen.has(n.id))];
};

const ActorAvatar = ({ actor, className = 'notifAvatar' }) => {
    if (actor?.profile_pic_url) {
        return <img className={className} src={actor.profile_pic_url} alt={actor.name || ''} />;
    }
    const initials = (actor?.name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    return <div className={`${className} notifInitials`}>{initials}</div>;
};

const ClientChip = ({ client }) => client?.name ? <span className='notifClientChip'>{client.name}</span> : null;

const Snippet = ({ text }) => {
    if (!text) return null;
    return (
        <>
            {text.split(/(@[\w.-]+)/g).map((part, i) => (
                part.startsWith('@') ? <span key={i} className='notifMention'>{part}</span> : <React.Fragment key={i}>{part}</React.Fragment>
            ))}
        </>
    );
};

// Swap the first occurrence of the client's name in the plain message for the chip.
const MessageWithClient = ({ message, client }) => {
    const name = client?.name;
    const index = name && message ? message.indexOf(name) : -1;
    if (index === -1) return <>{message}</>;
    return (
        <>
            {message.slice(0, index)}
            <ClientChip client={client} />
            {message.slice(index + name.length)}
        </>
    );
};

export const NotificationComponent = ({ setBoardSections, allPipelinesData, handleStageUpdate }) => {
    const [permission, setPermission] = useState(() => {
        return 'Notification' in window ? Notification?.permission : 'unsupported';
    });

    const { pipelineId } = useParams();
    const [searchParams] = useSearchParams();
    const appParam = searchParams.get('app');
    const checklistParam = searchParams.get('checklist');
    const location = useLocation();

    const [notifications, setNotifications] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [tabCounts, setTabCounts] = useState({});
    const [activeTab, setActiveTab] = useState('general');
    const [page, setPage] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    const [expandedDocs, setExpandedDocs] = useState([]);
    const [flagOpenId, setFlagOpenId] = useState(null);
    const [flagNote, setFlagNote] = useState('');
    const [flagging, setFlagging] = useState(false);

    const userId = useSelector((state) => state.user.userId);
    const notifyOp = useRef(null);
    const latestNotificationRef = useRef(null);
    const requestIdRef = useRef(0);
    // In unread-only mode each read shifts the server's pages, so paging forward would skip items.
    const readsSinceFetchRef = useRef(0);
    const toast = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [applicantData, setApplicantData] = useState();
    const [popupLoad, setPopupLoad] = useState(false);
    const [isUserCard, setIsUserCard] = useState(false);
    const [registeredUsersList, setRegisteredUsersList] = useState([]);
    const [hideReadItems, setHideReadItems] = useState(true); // default: show only unread
    const [allCustomFields, setAllCustomFields] = useState([]);
    const [loanDetailsCustomFields, setLoanDetailsCustomFields] = useState([]);
    const [realEstateTransactionsCustomFields, setRealEstateTransactionsCustomFields] = useState([]);

    const loanDetailsFields = [
        "Onedrive Link", "Gdrive Link", "Loan File ID", "Purchase Price", "Loan Amount",
        "Est. Taxes", "Loan Amount Status", "Closing Date", "Subject Property Address",
        "Sellers Concession", "First Payment Date", "Insurance Inspection Date"
    ];
    const realEstateTransactionFields = ["Real Estate Gdrive Link"];

    const requestPermission = () => {
        if (!("Notification" in window)) return;
        if (permission !== 'granted') {
            Notification?.requestPermission().then(setPermission);
        }
    };

    // Websocket ping: grab the newest item across all tabs for the alert, then refetch the open tab.
    const latestNotificationFn = async (userId) => {
        try {
            const response = await axios.get(`${getNotifications}${userId}?page=1&page_size=1`, jsonHeaders);
            if (response.status === 200) {
                const latest = response.data.results?.[0];
                setTotalCount(response.data.count || 0);
                setTabCounts(response.data.tab_counts || {});
                setRefreshTick(t => t + 1);
                if (!latest) return;
                if ("Notification" in window) {
                    if (Notification?.permission === "granted") {
                        showNotification(latest.message, latest.created_at);
                        toast.current.show({ severity: 'info', detail: latest.message, life: 3000 });
                    }
                } else {
                    toast.current.show({ severity: 'info', detail: latest.message, life: 3000 });
                }
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const fetchNotifications = async (reset = false) => {
        if (!userId) return;

        const nextPage = reset ? 1 : page + 1;
        const requestId = ++requestIdRef.current;
        let url = `${getNotifications}${userId}?tab=${activeTab}&page=${nextPage}&page_size=${pageSize}`;
        if (hideReadItems) {
            url += '&read=false';
        }

        try {
            setLoadingMore(true);
            const response = await axios.get(url, jsonHeaders);

            // A newer request (tab/filter switch) has superseded this one.
            if (requestId !== requestIdRef.current) return;

            if (response.status === 200) {
                const results = response.data.results || [];
                setTotalCount(response.data.count || 0);
                setTabCounts(response.data.tab_counts || {});
                // Replace by id: grouped uploads get updated in place server-side.
                setNotifications(prev => reset ? results : mergeById(prev, results));
                if (reset) readsSinceFetchRef.current = 0;
                setPage(nextPage);
                setHasMore(!!response.data.next);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            if (requestId === requestIdRef.current) setLoadingMore(false);
        }
    };

    // Reset and refetch when userId, tab or filter changes
    useEffect(() => {
        if (!userId) return;

        setNotifications([]);
        setPage(0);
        setHasMore(false);
        setExpandedDocs([]);
        setFlagOpenId(null);

        fetchNotifications(true);
    }, [userId, hideReadItems, activeTab]);

    useEffect(() => {
        if (refreshTick > 0) fetchNotifications(true);
    }, [refreshTick]);

    const loadMore = () => {
        if (!loadingMore && hasMore) {
            // After reads in unread-only mode, start over from page 1 so nothing is skipped.
            fetchNotifications(hideReadItems && readsSinceFetchRef.current > 0);
        }
    };

    // Everything visible was read but more unread exist on the server → pull the next batch.
    useEffect(() => {
        if (hideReadItems && notifications.length === 0 && hasMore && !loadingMore && readsSinceFetchRef.current > 0) {
            readsSinceFetchRef.current = 0; // once per batch of reads, so a failed request doesn't loop
            fetchNotifications(true);
        }
    }, [notifications.length, hasMore, loadingMore, hideReadItems]);

    const markAsRead = async (notification) => {
        if (!userId || notification.read) return;

        // Optimistic update
        setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setTotalCount(prev => Math.max(0, prev - 1));
        // General counts every notification, so it drops along with the item's own tab.
        const affectedTabs = new Set(['general', notification.tab].filter(Boolean));
        setTabCounts(prev => {
            const next = { ...prev };
            affectedTabs.forEach(tab => { next[tab] = Math.max(0, (prev[tab] || 0) - 1); });
            return next;
        });

        // If in unread-only mode → remove after small delay (visual feedback)
        if (hideReadItems) {
            readsSinceFetchRef.current += 1;
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
            }, 400);
        }

        try {
            await axios.get(`${notificationOpened}${notification.id}`, jsonHeaders);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const openFlagBox = (notification) => {
        setFlagOpenId(prev => prev === notification.id ? null : notification.id);
        setFlagNote('');
    };

    const submitFlag = async (notification) => {
        setFlagging(true);
        try {
            const note = flagNote.trim();
            const response = await axios.post(`${notificationFlag}${notification.id}`, note ? { note } : {}, jsonHeaders);
            setNotifications(prev =>
                prev.map(n => n.id === notification.id ? { ...n, is_flagged: true } : n)
            );
            setFlagOpenId(null);
            setFlagNote('');
            toast.current.show(response.data?.already_flagged
                ? { severity: 'info', detail: 'Already flagged for manager review', life: 3000 }
                : { severity: 'success', detail: 'Sent to manager review', life: 3000 });
        } catch (error) {
            const status = error.response?.status;
            if (status === 400) {
                setNotifications(prev =>
                    prev.map(n => n.id === notification.id ? { ...n, can_flag: false } : n)
                );
                setFlagOpenId(null);
            }
            const detail = status === 403 ? "You can't flag this notification"
                : status === 400 ? 'Manager review items cannot be flagged'
                : status === 404 ? 'Notification no longer exists'
                : 'Could not flag notification';
            toast.current.show({ severity: 'error', detail, life: 3000 });
        } finally {
            setFlagging(false);
        }
    };

    const toggleDocs = (id) => {
        setExpandedDocs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleMessage = useCallback((message) => {
        try {
            const parsedMessage = JSON.parse(message.data);
            const user_id = parsedMessage.message.userId;
            if (user_id === userId) {
                latestNotificationFn(userId);
            }
        } catch (err) {
            console.error('Error parsing websocket message:', err);
        }
    }, [userId]);

    useWebSocket('wss://backendapi.homesforsalemedia.com/ws/notifications/', {
        onOpen: () => console.log('Connected to WebSocket'),
        onMessage: handleMessage,
        onClose: () => console.log('Disconnected from WebSocket'),
        onError: (error) => console.error('WebSocket error:', error),
        shouldReconnect: () => true,
        reconnectAttempts: 10,
        reconnectInterval: 3000,
    });

    const showNotification = (message, createdTime) => {
        if (!("Notification" in window) || Notification.permission !== "granted") return;

        if (latestNotificationRef.current) {
            latestNotificationRef.current.close();
        }

        const options = {
            body: 'You have a new notification',
            icon: logo,
            tag: createdTime
        };

        const notification = new Notification(message, options);
        latestNotificationRef.current = notification;
    };

    const handleClick = (formId, notification, isUserCardId = false) => {
        markAsRead(notification);

        setIsUserCard(!!isUserCardId);
        const url = isUserCardId
            ? `${getUserOpportunityCard}${formId}?pipelineId=${pipelineId || 1}`
            : `${appSubmission}${formId}?pipelineId=${pipelineId || 1}`;

        setIsOpen(true);
        setPopupLoad(true);
        getAllCustomFields();

        axios.get(url, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.status === 200) {
                    setApplicantData(res.data);
                    setPopupLoad(false);
                }
            })
            .catch(console.error);

        axios.get(registeredUserList, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.status === 200) {
                    const options = res.data.map(user => ({
                        label: user.name,
                        value: user.user_id,
                        image: user.image
                    }));
                    setRegisteredUsersList(options);
                }
            })
            .catch(console.error);
    };

    const openNotificationTarget = (notification) => {
        if (notification.form_id) {
            handleClick(notification.form_id, notification);
        } else if (notification.user_card_id) {
            handleClick(notification.user_card_id, notification, true);
        } else {
            markAsRead(notification);
        }
    };

    const getAllCustomFields = () => {
        axios.get(getCustomFields, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.status === 200) {
                    const data = res.data;
                    setLoanDetailsCustomFields(data.filter(d => loanDetailsFields.includes(d.title)));
                    setRealEstateTransactionsCustomFields(data.filter(d => realEstateTransactionFields.includes(d.title)));
                    setAllCustomFields(data.filter(
                        d => !loanDetailsFields.includes(d.title) && !realEstateTransactionFields.includes(d.title)
                    ));
                }
            })
            .catch(console.error);
    };

    const openPopupFn = (formId, userOnboard = false) => {
        setIsOpen(true);
        setPopupLoad(true);
        getAllCustomFields();

        let url = userOnboard && appParam
            ? `${getUserOpportunityCard}${formId}?pipelineId=${pipelineId || 1}`
            : `${appSubmission}${formId}?pipelineId=${pipelineId || 1}`;

        setIsUserCard(userOnboard && !!appParam);

        axios.get(url, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.status === 200) {
                    setApplicantData(res.data);
                    setPopupLoad(false);
                    setTimeout(() => {
                        const el = document.getElementById(checklistParam);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 500);
                }
            })
            .catch(console.error);

        axios.get(registeredUserList, { headers: { 'Content-Type': 'application/json' } })
            .then((res) => {
                if (res.status === 200) {
                    const options = res.data.map(u => ({
                        label: u.name,
                        value: u.user_id,
                        image: u.image
                    }));
                    setRegisteredUsersList(options);
                }
            })
            .catch(console.error);
    };

    useEffect(() => {
        if (location.pathname.includes("onboarded-users") && appParam) {
            openPopupFn(appParam, true);
        } else if (appParam || checklistParam) {
            openPopupFn(appParam);
        }
    }, [location.pathname, appParam, checklistParam]);

    useEffect(() => {
        requestPermission();
    }, []);

    const renderMedia = (n) => {
        if (n.icon) {
            return (
                <div className='notifIconWrap'>
                    <span className='notifIcon'>{n.icon}</span>
                    <ActorAvatar actor={n.actor} className='notifAvatarBadge' />
                </div>
            );
        }
        return <ActorAvatar actor={n.actor} />;
    };

    const renderBody = (n) => {
        const time = <span className='notifTime'>{n.time_ago || n.created_at}</span>;
        const actorName = <b>{n.actor?.name}</b>;

        if (n.kind === 'comment_mention') {
            return (
                <>
                    <p className='notifLine'>{actorName} mentioned you{n.client && <> on client <ClientChip client={n.client} /></>}:</p>
                    <p className='notifLine'><Snippet text={n.snippet || n.message} /> {time}</p>
                </>
            );
        }

        if (n.kind === 'document_upload') {
            const count = n.document_count ?? n.documents?.length ?? 0;
            return (
                <p className='notifLine'>
                    {actorName} uploaded{' '}
                    {count === 1 && n.documents?.[0]
                        ? <i>{n.documents[0].file_name}</i>
                        : <b>{count} {count === 1 ? 'document' : 'documents'}</b>}
                    {n.client && <> for client <ClientChip client={n.client} /></>} {time}
                </p>
            );
        }

        if (n.kind === 'flagged_for_review') {
            const original = n.flagged_from;
            return (
                <>
                    <p className='notifLine'>{actorName} flagged a notification for review{n.client && <> on client <ClientChip client={n.client} /></>} {time}</p>
                    {n.flag_note && <p className='notifFlagNote'>“{n.flag_note}”</p>}
                    {original && (
                        <div className='notifQuoted'>
                            {original.actor?.name && <b>{original.actor.name}: </b>}
                            <Snippet text={original.snippet || original.message} />
                        </div>
                    )}
                </>
            );
        }

        return (
            <>
                <p className='notifLine'><MessageWithClient message={n.message} client={n.client} /> {time}</p>
                {n.snippet && <p className='notifLine'><Snippet text={n.snippet} /></p>}
            </>
        );
    };

    const renderDocuments = (n) => (
        <div className='notifDocs' onClick={(e) => e.stopPropagation()}>
            <div className='notifDocsHeader'>
                {n.documents.length} {n.documents.length === 1 ? 'document' : 'documents'} · This upload
            </div>
            {n.documents.map(doc => (
                <a key={doc.app_doc_id} className='notifDocRow' href={doc.file_url} target='_blank' rel='noopener noreferrer'>
                    <span className='notifDocBadge'>{fileBadge(doc.file_name)}</span>
                    <span className='notifDocInfo'>
                        <span className='notifDocName'>{doc.file_name}</span>
                        <span className='notifDocSize'>{formatFileSize(doc.file_size)}</span>
                    </span>
                    <i className='pi pi-external-link' />
                </a>
            ))}
        </div>
    );

    const renderItem = (n) => {
        const docCount = n.documents?.length || 0;
        const docsOpen = expandedDocs.includes(n.id);

        return (
            <div key={n.id} className={`notifItem ${n.read ? 'isRead' : ''}`} onClick={() => openNotificationTarget(n)}>
                <div className='notifMedia'>{renderMedia(n)}</div>
                <div className='notifContent'>
                    {renderBody(n)}

                    {(docCount > 0 || n.can_flag) && (
                        <div className='notifActions' onClick={(e) => e.stopPropagation()}>
                            {docCount > 0 && (
                                <button type='button' className='notifDocsToggle' onClick={() => toggleDocs(n.id)}>
                                    {docsOpen ? 'Hide' : 'Preview'} {docCount} {docCount === 1 ? 'document' : 'documents'}
                                    <i className={`pi ${docsOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                                </button>
                            )}
                            {n.can_flag && (
                                <button
                                    type='button'
                                    className={`notifFlagBtn ${n.is_flagged ? 'isFlagged' : ''}`}
                                    disabled={n.is_flagged}
                                    onClick={() => openFlagBox(n)}
                                >
                                    <i className={`pi ${n.is_flagged ? 'pi-flag-fill' : 'pi-flag'}`} />
                                    {n.is_flagged ? 'Flagged' : 'Flag'}
                                </button>
                            )}
                        </div>
                    )}

                    {flagOpenId === n.id && (
                        <div className='notifFlagBox' onClick={(e) => e.stopPropagation()}>
                            <InputTextarea
                                autoFocus
                                rows={2}
                                value={flagNote}
                                onChange={(e) => setFlagNote(e.target.value)}
                                placeholder='Add a note for your manager (optional)'
                            />
                            <div className='notifFlagBoxActions'>
                                <button type='button' className='notifFlagBtn' onClick={() => setFlagOpenId(null)} disabled={flagging}>Cancel</button>
                                <button type='button' className='notifFlagBtn notifFlagSend' onClick={() => submitFlag(n)} disabled={flagging}>
                                    {flagging ? 'Sending...' : 'Send to manager review'}
                                </button>
                            </div>
                        </div>
                    )}

                    {docsOpen && docCount > 0 && renderDocuments(n)}
                </div>
                {!n.read ? (
                    <button
                        type='button'
                        className='notifUnreadDot'
                        title='Mark as read'
                        onClick={(e) => { e.stopPropagation(); markAsRead(n); }}
                    />
                ) : <span className='notifDotSpacer' />}
            </div>
        );
    };

    const activeTabMeta = NOTIFICATION_TABS.find(t => t.id === activeTab);
    const visibleNotifications = hideReadItems ? notifications.filter(n => !n.read) : notifications;
    const groups = groupByDay(visibleNotifications);

    return (
        <div>
            <Toast ref={toast} />
            <Button
                onClick={(e) => {
                    notifyOp.current.toggle(e);
                    fetchNotifications(true); // refresh on panel open
                }}
                style={{ boxShadow: "none", marginRight: ".5rem", background: "transparent" }}
            >
                <i className="pi pi-bell p-overlay-badge" style={{ fontSize: '1.5rem' }}>
                    <Badge style={{ background: "red", color: "white" }} value={totalCount}></Badge>
                </i>
            </Button>

            <OverlayPanel ref={notifyOp} showCloseIcon dismissable={false} className='notifPanel'>
                <div className='notifHeader'>
                    <h2>Notifications</h2>
                    <div className='notifHeaderMeta'>
                        <span className='notifHeaderCount'>
                            <span className='notifHeaderDot' />
                            {tabCounts[activeTab] || 0} in {activeTabMeta.short}
                        </span>
                        <label className='notifUnreadToggle'>
                            Unread only
                            <InputSwitch checked={hideReadItems} onChange={(e) => setHideReadItems(e.value)} />
                        </label>
                    </div>
                </div>

                <div className='notifTabs'>
                    {NOTIFICATION_TABS.map(tab => (
                        <button
                            key={tab.id}
                            type='button'
                            className={`notifTab ${tab.variant ? `notifTab-${tab.variant}` : ''} ${activeTab === tab.id ? 'isActive' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon && <span className='notifTabIcon'>{tab.icon}</span>}
                            {tab.label}
                            <span className='notifTabCount'>{tabCounts[tab.id] || 0}</span>
                        </button>
                    ))}
                </div>

                <div className='notifScroll'>
                    {visibleNotifications.length === 0 && !loadingMore && (
                        <div className='notifEmpty'>
                            <i className='pi pi-file' />
                            <h3>{hideReadItems ? `No unread ${activeTabMeta.short} notifications` : `Nothing in ${activeTabMeta.short} yet`}</h3>
                            <p>{activeTabMeta.emptyText}</p>
                        </div>
                    )}

                    {groups.map((group, i) => (
                        <div key={`${group.label}-${i}`} className='notifGroup'>
                            <h4 className='notifGroupLabel'>{group.label}</h4>
                            {group.items.map(renderItem)}
                        </div>
                    ))}

                    {hasMore ? (
                        <div className='notifFooter'>
                            <button type='button' className='notifFlagBtn' disabled={loadingMore} onClick={loadMore}>
                                {loadingMore ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    ) : visibleNotifications.length > 0 && (
                        <p className='notifFooter'>You're up to date.</p>
                    )}
                </div>
            </OverlayPanel>

            <AppModal visible={isOpen} onHide={() => setIsOpen(false)}>
                <div style={{ marginTop: '1rem', minHeight: "70vh" }}>
                    {!popupLoad ? (
                        isUserCard ? (
                            <UserPopup
                                applicantData={applicantData}
                                pipelineId={pipelineId}
                                usersList={registeredUsersList}
                                userId={userId}
                                setBoardSections={setBoardSections}
                                handleClose={() => setIsOpen(false)}
                                toast={toast}
                            />
                        ) : (
                            <AppPopup
                                applicantData={applicantData}
                                pipelineId={pipelineId}
                                allPipelinesData={allPipelinesData}
                                handleStageUpdate={handleStageUpdate}
                                allCustomFields={allCustomFields}
                                loanDetailsCustomFields={loanDetailsCustomFields}
                                realEstateTransactionsCustomFields={realEstateTransactionsCustomFields}
                                isFromSearch={true}
                                setApplicantData={setApplicantData}
                                setBoardSections={setBoardSections}
                                usersList={registeredUsersList}
                                userId={userId}
                                handleClose={() => setIsOpen(false)}
                                toast={toast}
                            />
                        )
                    ) : (
                        <FlexComponent flexDirection="column" justifyContent="center">
                            <div style={{ minWidth: "40vw", height: "100%" }} className='loadingAppItem' />
                        </FlexComponent>
                    )}
                </div>
            </AppModal>
        </div>
    );
};
